import {
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/react";
import type { GuidedEditPayload } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/components/CreateCampaignModal";
import type {
  FacebookPage,
  InstagramAccount,
} from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/types";
import CreativeBlockEditor from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/components/CreativeBlockEditor";
import type { CreativeBlock } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/guidedFlow/types";
import {
  hasMixedGuidedCreativeMediaTypes,
  MATCHING_CREATIVE_MEDIA_TYPES_ERROR,
} from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/utils/validateCreativeMediaType";
import { useEffect, useMemo, useRef, useState } from "react";

type GuidedCreative = GuidedEditPayload["creatives"][number];

type EditAdCreativeModalProps = {
  isOpen: boolean;
  isSaving?: boolean;
  /** When true, Facebook pages for the ad account are still loading. */
  isFacebookPagesLoading?: boolean;
  /** When true, Instagram accounts for the selected page are still loading. */
  isInstagramAccountsLoading?: boolean;
  /** When true, another creative in the same ad set has Instagram set; this modal cannot clear Instagram. */
  otherAdsInAdsetHaveInstagram?: boolean;
  creative: GuidedCreative;
  pages: FacebookPage[];
  instagramAccounts: InstagramAccount[];
  createUploadUrl: (
    file: File,
    callback: (params: {
      signedUrl?: string;
      fileKey?: string;
      file_key?: string;
      cdn_url?: string;
    }) => void
  ) => void;
  onClose: () => void;
  onSave: (creative: GuidedCreative) => void | Promise<void>;
  onFacebookPageChange?: (
    facebookPageId: string
  ) => void | Promise<InstagramAccount[] | void>;
};

export default function EditAdCreativeModal({
  isOpen,
  isSaving = false,
  isFacebookPagesLoading = false,
  isInstagramAccountsLoading = false,
  otherAdsInAdsetHaveInstagram = false,
  creative,
  pages,
  instagramAccounts,
  createUploadUrl,
  onClose,
  onSave,
  onFacebookPageChange,
}: EditAdCreativeModalProps) {
  const [value, setValue] = useState<CreativeBlock>({
    primaryText: "",
    fbPage: null,
    instagramAccount: null,
  });
  const [editingCreativeName, setEditingCreativeName] = useState<{
    index: number;
    value: string;
  } | null>(null);
  const uploadRefs = useRef<
    Record<string, { fileKey?: string; cdn_url?: string }>
  >({});
  const lastModalSourceKeyRef = useRef("");

  const modalSourceKey = useMemo(
    () =>
      [
        creative.fb_page_id,
        creative.instagram_id,
        creative.primary_text ?? "",
        creative.square_url ?? "",
        creative.square_file_key ?? "",
        creative.portrait_url ?? "",
        creative.portrait_file_key ?? "",
        pages
          .map((p) => p.id)
          .sort()
          .join(","),
      ].join("|"),
    [creative, pages]
  );

  /**
   * When the server creative or page list identity changes, replace the editor from
   * props. When only Instagram rows load (same source key), merge account objects
   * without overwriting a Facebook page the user already picked in the modal.
   */
  useEffect(() => {
    if (!isOpen) {
      lastModalSourceKeyRef.current = "";
      return;
    }

    const replaceFromServer = lastModalSourceKeyRef.current !== modalSourceKey;
    if (replaceFromServer) {
      lastModalSourceKeyRef.current = modalSourceKey;
    }

    setValue((prev) => {
      if (replaceFromServer) {
        const mappedPage =
          pages.find((page) => page.id === creative.fb_page_id) ?? null;
        const igId = creative.instagram_id?.trim();
        const mappedInstagram = igId
          ? instagramAccounts.find((account) => account.id === igId) ?? null
          : null;
        return {
          name: creative.name,
          primaryText: creative.primary_text ?? "",
          squareMediaUrl: creative.square_url,
          squareMediaFileKey: creative.square_file_key,
          squareMediaType: creative.square_media_type,
          verticalMediaUrl: creative.portrait_url,
          verticalMediaFileKey: creative.portrait_file_key,
          verticalMediaType: creative.portrait_media_type,
          fbPage: mappedPage,
          instagramAccount: mappedInstagram,
        };
      }

      const wantedId =
        String(prev.instagramAccount?.id ?? "").trim() ||
        (prev.fbPage?.id === creative.fb_page_id
          ? String(creative.instagram_id ?? "").trim()
          : "");
      const mapped = wantedId
        ? instagramAccounts.find((a) => a.id === wantedId) ?? null
        : null;
      if (mapped && prev.instagramAccount?.id !== mapped.id) {
        return { ...prev, instagramAccount: mapped };
      }
      return prev;
    });

    if (replaceFromServer) {
      setEditingCreativeName(null);
      uploadRefs.current = {};
    }
  }, [creative, instagramAccounts, isOpen, modalSourceKey, pages]);

  const pageOptions = useMemo(
    () => pages.map((page) => ({ label: page.name, value: page })),
    [pages]
  );
  const instagramOptions = useMemo(
    () =>
      instagramAccounts.map((instagramAccount) => ({
        label: instagramAccount.username || instagramAccount.name || "",
        value: instagramAccount,
      })),
    [instagramAccounts]
  );

  const createUploadUrlForSlot =
    (slotKey: string) =>
    ({
      file,
      callback,
    }: {
      file: File;
      callback: (r: { signedUrl: string }) => void | Promise<string | void>;
    }) =>
      new Promise<{ signedUrl: string; fileKey: string }>((resolve) => {
        createUploadUrl(file, (res: any) => {
          const fileKey = res?.fileKey ?? res?.file_key ?? "";
          const signedUrl = res?.signedUrl ?? res?.signed_url ?? "";
          const cdn_url = res?.cdn_url ?? "";
          uploadRefs.current[slotKey] = { fileKey, cdn_url };
          callback({ signedUrl });
          resolve({ signedUrl, fileKey });
        });
      });

  const selectContainerStyles = {
    container: () => ({
      position: "relative" as const,
      width: "100%",
      backgroundColor: "white",
      zIndex: 2,
      cursor: "pointer",
    }),
    dropdownIndicator: (provided: Record<string, unknown>) => ({
      ...provided,
      backgroundColor: "white",
      color: "gray.500",
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
  };

  const toGuidedCreative = (): GuidedCreative => {
    const hasSquareAsset = Boolean(
      value.squareMediaUrl?.trim() || value.squareMediaFileKey?.trim()
    );
    const hasPortraitAsset = Boolean(
      value.verticalMediaUrl?.trim() || value.verticalMediaFileKey?.trim()
    );
    return {
      ...(value.name ? { name: value.name } : {}),
      primary_text: value.primaryText,
      ...(hasSquareAsset && value.squareMediaUrl
        ? { square_url: value.squareMediaUrl }
        : {}),
      ...(hasSquareAsset && value.squareMediaFileKey
        ? { square_file_key: value.squareMediaFileKey }
        : {}),
      ...(hasSquareAsset && value.squareMediaType
        ? { square_media_type: value.squareMediaType }
        : {}),
      ...(hasPortraitAsset && value.verticalMediaUrl
        ? { portrait_url: value.verticalMediaUrl }
        : {}),
      ...(hasPortraitAsset && value.verticalMediaFileKey
        ? { portrait_file_key: value.verticalMediaFileKey }
        : {}),
      ...(hasPortraitAsset && value.verticalMediaType
        ? { portrait_media_type: value.verticalMediaType }
        : {}),
      fb_page_id: value.fbPage?.id ?? creative.fb_page_id ?? "",
      // Form state only: if the user clears Instagram, we must send "" so the
      // update path strips identity/placements — do not fall back to the prop.
      instagram_id: value.instagramAccount?.id?.trim() ?? "",
    };
  };

  const hasMixedMediaTypes = useMemo(
    () => hasMixedGuidedCreativeMediaTypes(value),
    [value]
  );
  const hasPrimaryText = value.primaryText.trim().length > 0;
  const hasFacebookPage = Boolean(value.fbPage?.id);
  const hasAtLeastOneMedia =
    (Boolean(value.squareMediaUrl || value.squareMediaFileKey) &&
      Boolean(value.squareMediaType)) ||
    (Boolean(value.verticalMediaUrl || value.verticalMediaFileKey) &&
      Boolean(value.verticalMediaType));
  const isFormValid =
    hasAtLeastOneMedia &&
    hasPrimaryText &&
    hasFacebookPage &&
    !hasMixedMediaTypes;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit Ad Creative</ModalHeader>
        <ModalBody>
          <CreativeBlockEditor
            creative={value}
            index={0}
            totalCreatives={1}
            pageOptions={pageOptions}
            instagramOptions={instagramOptions}
            createUploadUrlForSlot={createUploadUrlForSlot}
            uploadRefs={uploadRefs}
            onUpdate={(_, patch) =>
              setValue((prev) => ({
                ...prev,
                ...patch,
              }))
            }
            onRemove={() => {}}
            onAddMore={() => {}}
            onFacebookPageChange={onFacebookPageChange}
            editingName={editingCreativeName}
            onEditingNameChange={setEditingCreativeName}
            mediaValidationMessage={
              hasMixedMediaTypes ? MATCHING_CREATIVE_MEDIA_TYPES_ERROR : null
            }
            allowCreativeCountChanges={false}
            instagramAccountClearDisabled={otherAdsInAdsetHaveInstagram}
            isFacebookPagesLoading={isFacebookPagesLoading}
            isInstagramAccountsLoading={isInstagramAccountsLoading}
            selectContainerStyles={selectContainerStyles}
          />
        </ModalBody>
        <ModalFooter>
          <HStack spacing={3}>
            <Button variant="ghost" onClick={onClose} isDisabled={isSaving}>
              Cancel
            </Button>
            <Button
              bg="gray.900"
              color="white"
              _hover={{ bg: "gray.800" }}
              onClick={() => {
                if (!isFormValid) return;
                onSave(toGuidedCreative());
              }}
              isLoading={isSaving}
              isDisabled={
                isFacebookPagesLoading ||
                isInstagramAccountsLoading ||
                isSaving ||
                !isFormValid
              }
            >
              Save
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
